/**
 * The offline write queue.
 *
 * Every write the app makes goes here first and renders optimistically, so an
 * engineer standing in a switchroom with no signal never waits and never
 * retypes. The queue drains when there is a connection.
 *
 * The property that matters more than any other: **a retry after a dropped
 * connection must not create a second record.** A phone that loses signal
 * mid-upload cannot know whether the write landed. If the queue simply retried,
 * a board would end up with two result lines for one check — and two readings
 * for one check is a report nobody can sign, discovered weeks later.
 *
 * So every mutation carries an `offlineSyncId` generated *at capture*, never at
 * send. Before creating anything the queue asks the server whether that id is
 * already there; if it is, the write already landed and the queue adopts the
 * existing row instead of making another.
 */

export type MutationKind = 'create' | 'update';

export interface Mutation {
  /** The idempotency key. Generated when the engineer acts, never when the write is sent. */
  offlineSyncId: string;
  kind: MutationKind;
  list: string;
  /** For an update: the SharePoint item id. */
  itemId?: number;
  fields: Record<string, unknown>;
  /** Mutations that must land before this one — a result line needs its instance. */
  dependsOn?: readonly string[];
  createdAt: string;
  attempts: number;
  /** Set once the row exists on the server. */
  serverId?: number;
  status: 'queued' | 'sent' | 'parked';
  lastError?: string;
  /** Human label for the sync screen. */
  label?: string;
}

export interface QueueStore {
  all(): Promise<Mutation[]>;
  put(m: Mutation): Promise<void>;
  remove(offlineSyncId: string): Promise<void>;
}

export interface SyncTransport {
  /** Returns the existing item id for this sync id, or null. */
  findBySyncId(list: string, offlineSyncId: string): Promise<number | null>;
  create(list: string, fields: Record<string, unknown>): Promise<number>;
  update(list: string, itemId: number, fields: Record<string, unknown>): Promise<void>;
}

export interface DrainResult {
  sent: number;
  adopted: number;
  parked: number;
  remaining: number;
  /** Set when the drain stopped early because the connection is gone. */
  stoppedOffline: boolean;
}

/** Errors the queue should keep retrying. Anything else is our mistake and parks. */
function isRetryable(err: unknown): boolean {
  if (err && typeof err === 'object') {
    const e = err as { retryable?: boolean; name?: string };
    if (typeof e.retryable === 'boolean') return e.retryable;
    // A fetch that never reached the server throws TypeError. That is exactly
    // the dropped-signal case, and the one that must never duplicate.
    if (e.name === 'TypeError' || e.name === 'AbortError') return true;
  }
  return false;
}

export const MAX_ATTEMPTS = 6;

export class SyncQueue {
  constructor(
    private readonly store: QueueStore,
    private readonly transport: SyncTransport,
    private readonly newId: () => string = () => crypto.randomUUID(),
  ) {}

  /** Records a write locally and returns its idempotency key. Never touches the network. */
  async enqueue(
    input: Omit<Mutation, 'offlineSyncId' | 'createdAt' | 'attempts' | 'status'> & { offlineSyncId?: string },
  ): Promise<Mutation> {
    const m: Mutation = {
      offlineSyncId: input.offlineSyncId ?? this.newId(),
      createdAt: new Date().toISOString(),
      attempts: 0,
      status: 'queued',
      ...input,
    };
    // The key travels with the record so the server can be asked about it later.
    if (m.kind === 'create') m.fields = { ...m.fields, OfflineSyncId: m.offlineSyncId };
    await this.store.put(m);
    return m;
  }

  async pending(): Promise<Mutation[]> {
    return (await this.store.all()).filter((m) => m.status === 'queued');
  }

  async parked(): Promise<Mutation[]> {
    return (await this.store.all()).filter((m) => m.status === 'parked');
  }

  /** Puts a parked mutation back in the queue — the retry button on the sync screen. */
  async unpark(offlineSyncId: string): Promise<void> {
    const m = (await this.store.all()).find((x) => x.offlineSyncId === offlineSyncId);
    if (!m) return;
    await this.store.put({ ...m, status: 'queued', attempts: 0, ...(m.lastError ? { lastError: m.lastError } : {}) });
  }

  /**
   * Sends what it can, oldest first, respecting dependencies.
   *
   * Stops at the first genuinely offline failure rather than burning through the
   * queue: if the connection is gone, the next one will fail too, and the only
   * thing achieved by continuing is a pile of attempt counters.
   */
  async drain(): Promise<DrainResult> {
    const out: DrainResult = { sent: 0, adopted: 0, parked: 0, remaining: 0, stoppedOffline: false };
    const done = new Map<string, number>();

    for (const m of (await this.store.all()).filter((x) => x.status === 'sent' && x.serverId !== undefined)) {
      done.set(m.offlineSyncId, m.serverId as number);
    }

    const queue = (await this.pending()).sort((a, b) => a.createdAt.localeCompare(b.createdAt));

    for (const m of queue) {
      if (out.stoppedOffline) break;
      const missing = (m.dependsOn ?? []).filter((d) => !done.has(d));
      if (missing.length) continue; // its turn comes after the thing it needs

      try {
        if (m.kind === 'create') {
          // Ask first. This is the whole idempotency guarantee.
          const existing = await this.transport.findBySyncId(m.list, m.offlineSyncId);
          if (existing !== null) {
            done.set(m.offlineSyncId, existing);
            await this.store.put({ ...m, status: 'sent', serverId: existing });
            out.adopted += 1;
            continue;
          }
          const id = await this.transport.create(m.list, this.resolve(m.fields, done));
          done.set(m.offlineSyncId, id);
          await this.store.put({ ...m, status: 'sent', serverId: id });
          out.sent += 1;
        } else {
          const id = m.itemId ?? done.get(m.dependsOn?.[0] ?? '');
          if (id === undefined) {
            await this.store.put({ ...m, status: 'parked', lastError: 'update with no item id' });
            out.parked += 1;
            continue;
          }
          await this.transport.update(m.list, id, this.resolve(m.fields, done));
          await this.store.put({ ...m, status: 'sent', serverId: id });
          out.sent += 1;
        }
      } catch (err) {
        const attempts = m.attempts + 1;
        const message = err instanceof Error ? err.message : String(err);
        if (isRetryable(err) && attempts < MAX_ATTEMPTS) {
          await this.store.put({ ...m, attempts, lastError: message });
          out.stoppedOffline = true;
        } else {
          // Parked, not dropped. A capture an engineer made is never thrown away
          // because the server disliked it — it waits on the sync screen.
          await this.store.put({ ...m, attempts, status: 'parked', lastError: message });
          out.parked += 1;
        }
      }
    }

    out.remaining = (await this.pending()).length;
    return out;
  }

  /** Swaps `{ syncRef: '<id>' }` placeholders for the real ids the server just gave us. */
  private resolve(fields: Record<string, unknown>, done: Map<string, number>): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(fields)) {
      if (v && typeof v === 'object' && 'syncRef' in (v as Record<string, unknown>)) {
        const ref = String((v as { syncRef: unknown }).syncRef);
        const id = done.get(ref);
        if (id === undefined) throw new Error(`unresolved reference ${ref} for ${k}`);
        out[k] = id;
      } else {
        out[k] = v;
      }
    }
    return out;
  }
}

/** A reference to something still in the queue, resolved to a real id at send time. */
export const ref = (offlineSyncId: string) => ({ syncRef: offlineSyncId });

/** In-memory store. Real storage is IndexedDB; this is what the tests run on. */
export class MemoryQueueStore implements QueueStore {
  private readonly rows = new Map<string, Mutation>();
  async all(): Promise<Mutation[]> {
    return [...this.rows.values()];
  }
  async put(m: Mutation): Promise<void> {
    this.rows.set(m.offlineSyncId, m);
  }
  async remove(id: string): Promise<void> {
    this.rows.delete(id);
  }
}
